/**
 * CDPStreamCanvas — Renders JPEG frames from CDP screencast stream.
 * Handles click events for element picking and displays element highlights.
 */

import React, { useRef, useCallback, useState } from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import {
  FiberManualRecord as DotIcon,
  HourglassEmpty as RunningIcon,
} from '@mui/icons-material';

const CDPStreamCanvas = ({
  frame,           // base64 JPEG string
  metadata,        // CDP frame metadata
  streaming,
  connected,
  currentUrl,
  pickedElement,
  executionProgress,
  onCanvasClick,
  scriptName = '',
  elapsed = '',
}) => {
  const imgRef = useRef(null);
  const [hovering, setHovering] = useState(false);

  // Convert canvas click coordinates to page coordinates
  const handleClick = useCallback(
    (e) => {
      if (!onCanvasClick || !imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      const imgWidth = imgRef.current.naturalWidth || 1280;
      const imgHeight = imgRef.current.naturalHeight || 720;
      const scaleX = imgWidth / rect.width;
      const scaleY = imgHeight / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;
      onCanvasClick(x, y);
    },
    [onCanvasClick]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: '#fafafa',
        }}
      >
        <DotIcon
          sx={{
            color: streaming ? '#4caf50' : connected ? '#ff9800' : '#f44336',
            fontSize: 14,
            mr: 1,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: streaming ? '#4caf50' : '#757575',
            mr: 2,
          }}
        >
          {streaming ? 'Live Server Browser' : connected ? 'Connected' : 'Disconnected'}
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
          {scriptName}
        </Typography>
        {elapsed && (
          <Chip
            label={elapsed}
            size="small"
            variant="outlined"
            sx={{ fontFamily: 'monospace' }}
          />
        )}
      </Box>

      {/* CDP Stream viewport */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#1a1a2e',
          color: '#8892b0',
          position: 'relative',
          overflow: 'hidden',
          cursor: streaming ? 'crosshair' : 'default',
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        {/* Fake browser chrome */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 36,
            bgcolor: '#2d2d44',
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            gap: 1,
            zIndex: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.6 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f57' }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840' }} />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: '#1a1a2e',
              borderRadius: 1,
              px: 1.5,
              py: 0.3,
              fontSize: '0.75rem',
              color: '#8892b0',
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentUrl || 'about:blank'}
          </Box>
        </Box>

        {/* Frame or placeholder */}
        {frame ? (
          <img
            ref={imgRef}
            src={`data:image/jpeg;base64,${frame}`}
            alt="CDP Screencast"
            onClick={handleClick}
            style={{
              marginTop: 36,
              width: '100%',
              height: 'calc(100% - 36px)',
              objectFit: 'contain',
              cursor: 'crosshair',
            }}
          />
        ) : (
          <Box sx={{ mt: 4, textAlign: 'center', px: 3 }}>
            <Typography variant="h6" sx={{ color: '#ccd6f6', mb: 2 }}>
              {streaming ? 'Waiting for frames...' : 'No active screencast'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#4a5568' }}>
              {streaming
                ? 'CDP Screencast starting up...'
                : 'Click "Play" or start a session to see the server browser here'}
            </Typography>
          </Box>
        )}

        {/* Picked element highlight overlay */}
        {pickedElement && hovering && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 44,
              left: 8,
              right: 8,
              bgcolor: 'rgba(25, 118, 210, 0.9)',
              color: '#fff',
              px: 1.5,
              py: 0.8,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              zIndex: 3,
            }}
          >
            Picked: {pickedElement.selector}
            {pickedElement.textContent && (
              <Typography variant="caption" sx={{ display: 'block', color: '#bbdefb' }}>
                Text: "{pickedElement.textContent.substring(0, 60)}"
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Bottom status bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderTop: '1px solid #e0e0e0',
          bgcolor: executionProgress ? '#fff8e1' : '#fafafa',
        }}
      >
        {executionProgress ? (
          <>
            <RunningIcon sx={{ fontSize: 16, color: '#ff9800', mr: 1 }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: '#e65100' }}>
              Step {executionProgress.step}/{executionProgress.total}: {executionProgress.stepName}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {executionProgress.elapsed || ''}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary">
              {streaming ? 'Live stream active' : 'Stream idle'}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {connected ? 'WS connected' : 'WS disconnected'}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CDPStreamCanvas;
